/**
 * "A scene never edits the inside of a reference" — the editor's binding of
 * `trace-viewer/editor`'s copy-on-edit policy (SCENE_ROOT_SPEC §2.3
 * "Editing").
 *
 * The policy is the viewer's: which edits land inside a placed reference,
 * which sessions it governs, and how the copy and its undo entry are built.
 * What only the editor knows is the folder the copy goes into — the open CMS
 * scene's, through {@link CmsManager.folderUploadTarget} — and how to tell the
 * user. A session with no CMS scene (a local `.trace` opened from disk) has
 * no folder to copy into, so the guard stands down there and the edit goes
 * ahead as before. A legacy session copies nothing (the viewer's rule).
 */

import type { SceneObject } from 'trace-viewer/scene';
import {
    copyForInsideEdit as copyReferencesForInsideEdit,
    getUndoManager,
    needsReferenceCopy as editLandsInsideReference,
} from 'trace-viewer/editor';
import { showSpinnerToast, showToast } from '../toast';
import { getActiveCmsManager } from './active-manager';

/** Whether an edit to `object` would land inside a reference this session can copy — checked synchronously. */
export function needsReferenceCopy(object: SceneObject | null | undefined): boolean {
    return getActiveCmsManager() !== null && editLandsInsideReference(object);
}

/**
 * Copy every reference the pending edit would land inside, so the edit can be
 * applied to nodes this scene owns. The copy is its own undo entry, pushed
 * before the edit's.
 *
 * The objects the caller is about to edit keep their identity across the copy,
 * so `targets` are still the right `SceneObject`s afterwards.
 *
 * @returns `true` when the edit may proceed — nothing needed copying, every
 *   copy succeeded, or the guard stood down. `false` when a copy failed, in
 *   which case the caller must abandon the edit rather than write into a
 *   reference.
 */
export async function copyForInsideEdit(
    targets: SceneObject | readonly SceneObject[],
): Promise<boolean> {
    const list: readonly SceneObject[] = Array.isArray(targets) ? targets : [targets];
    const folder = getActiveCmsManager()?.folderUploadTarget();
    const dismiss = list.some(editLandsInsideReference) && folder
        ? showSpinnerToast('Copying the placed asset into this scene…')
        : null;
    try {
        const result = await copyReferencesForInsideEdit(list, folder ? { folder } : {});
        if ('refused' in result) {
            const { code, message } = result.refused;
            if (code === 'NO_FOLDER') return true;
            console.error(`[ReferenceEdit] ${message} — the edit was not applied.`);
            if (dismiss) dismiss(`${message} — the edit was not applied`, 'error');
            else showToast(`${message} — the edit was not applied`, 'error');
            return false;
        }
        if (!result.command) return true;
        getUndoManager().push(result.command);
        const names = [...result.copied.keys()].map(object => object.name).join(', ');
        if (result.orphaned.length > 0) {
            console.error(
                `[ReferenceEdit] ${result.orphaned.length} file(s) could not be copied into this scene's folder, `
                + `so a save will not carry them: ${result.orphaned.join(', ')}`,
            );
            dismiss?.(`Copied ${names}, but ${result.orphaned.length} file(s) could not be brought across — see console`, 'error');
        } else {
            dismiss?.(`Copied ${names} into this scene so it can be edited`, 'success');
        }
        console.log(`[ReferenceEdit] Copied ${names} into this scene before an edit inside it`);
        return true;
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('[ReferenceEdit] Copy failed — the edit was not applied:', error);
        dismiss?.(`Copy to scene failed: ${message}`, 'error');
        return false;
    }
}
